import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import KatexRenderer from './KatexRenderer';
import OptimizedImage from './OptimizedImage';

// Calculator-style Input Component
const CalculatorInput = React.memo(({ value, onChange, onValidation, disabled, allowDecimal = true, allowNegative = true, placeholder = "Enter your answer", unit }) => {
  const inputRef = useRef(null);
  const [cursorPosition, setCursorPosition] = useState(0);
  const cursorUpdateTimeoutRef = useRef(null);

  // Sync cursor position only when input is focused and value changes
  useEffect(() => {
    if (inputRef.current && document.activeElement === inputRef.current) {
      // Clear any pending timeout
      if (cursorUpdateTimeoutRef.current) {
        clearTimeout(cursorUpdateTimeoutRef.current);
      }
      // Use requestAnimationFrame for smoother updates
      cursorUpdateTimeoutRef.current = setTimeout(() => {
        if (inputRef.current && document.activeElement === inputRef.current) {
          const currentPos = inputRef.current.selectionStart || cursorPosition;
          if (currentPos !== cursorPosition) {
            inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
          }
        }
      }, 0);
    }
    
    return () => {
      if (cursorUpdateTimeoutRef.current) {
        clearTimeout(cursorUpdateTimeoutRef.current);
      }
    };
  }, [cursorPosition]);

  const handleButtonClick = useCallback((char) => {
    if (disabled) return;
    
    // Get current cursor position from input directly
    const currentCursorPos = inputRef.current?.selectionStart ?? cursorPosition;
    const currentValue = value || '';
    const newValue = currentValue.slice(0, currentCursorPos) + char + currentValue.slice(currentCursorPos);
    const newCursorPos = currentCursorPos + 1;
    
    // Validate the new value
    if (onValidation) {
      const validationResult = onValidation(newValue);
      if (validationResult.isValid) {
        onChange(newValue);
        // Update cursor position after state update
        requestAnimationFrame(() => {
          if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
            setCursorPosition(newCursorPos);
          }
        });
      }
    } else {
      onChange(newValue);
      // Update cursor position after state update
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
          setCursorPosition(newCursorPos);
        }
      });
    }
  }, [disabled, value, cursorPosition, onChange, onValidation]);

  const handleBackspace = useCallback(() => {
    if (disabled) return;
    
    const currentValue = value || '';
    const currentCursorPos = inputRef.current?.selectionStart ?? cursorPosition;
    
    if (!currentValue || currentCursorPos === 0) return;
    
    const newValue = currentValue.slice(0, currentCursorPos - 1) + currentValue.slice(currentCursorPos);
    const newCursorPos = Math.max(0, currentCursorPos - 1);
    
    if (onValidation) {
      const validationResult = onValidation(newValue);
      if (validationResult.isValid || newValue === '') {
        onChange(newValue);
        requestAnimationFrame(() => {
          if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
            setCursorPosition(newCursorPos);
          }
        });
      }
    } else {
      onChange(newValue);
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
          setCursorPosition(newCursorPos);
        }
      });
    }
  }, [disabled, value, cursorPosition, onChange, onValidation]);

  const handleClearAll = useCallback(() => {
    if (disabled) return;
    onChange('');
    setCursorPosition(0);
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.setSelectionRange(0, 0);
    }
  }, [disabled, onChange]);

  const handleCursorLeft = useCallback(() => {
    if (disabled) return;
    const newPos = Math.max(0, cursorPosition - 1);
    setCursorPosition(newPos);
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newPos, newPos);
      }
    });
  }, [disabled, cursorPosition]);

  const handleCursorRight = useCallback(() => {
    if (disabled) return;
    const maxPos = (value || '').length;
    const newPos = Math.min(maxPos, cursorPosition + 1);
    setCursorPosition(newPos);
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newPos, newPos);
      }
    });
  }, [disabled, value, cursorPosition]);

  const handleInputChange = useCallback((e) => {
    if (disabled) return;
    const newValue = e.target.value;
    const newCursorPos = e.target.selectionStart;
    
    if (onValidation) {
      const validationResult = onValidation(newValue);
      if (validationResult.isValid || newValue === '') {
        onChange(newValue);
        setCursorPosition(newCursorPos);
      }
    } else {
      onChange(newValue);
      setCursorPosition(newCursorPos);
    }
  }, [disabled, onChange, onValidation]);

  const handleInputClick = useCallback((e) => {
    if (disabled) return;
    const pos = e.target.selectionStart || 0;
    setCursorPosition(pos);
  }, [disabled]);

  const handleInputKeyDown = useCallback((e) => {
    if (disabled) return;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      handleCursorLeft();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      handleCursorRight();
    } else if (e.key === 'Backspace') {
      e.preventDefault();
      handleBackspace();
    }
  }, [disabled, handleCursorLeft, handleCursorRight, handleBackspace]);

  return (
    <div className="space-y-2 w-[30%]">
      {/* Input Field */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value || ''}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onKeyDown={handleInputKeyDown}
          onFocus={(e) => {
            const pos = e.target.selectionStart || 0;
            setCursorPosition(pos);
          }}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-3 py-2 border border-gray-400 rounded-lg text-sm font-normal bg-white text-black placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500 ${unit ? 'pr-12' : ''}`}
          readOnly={disabled}
        />
        {unit && (
          <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-700 text-sm">
            {unit}
          </span>
        )}
      </div>

      {/* Calculator Keypad */}
      <div className="bg-gray-200 rounded-lg p-2 border border-gray-400">
        {/* Backspace Button */}
        <button
          type="button"
          onClick={handleBackspace}
          disabled={disabled}
          className="w-full mb-1.5 px-2 py-1.5 bg-gray-300 hover:bg-gray-400 rounded text-sm font-medium text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Backspace
        </button>

        {/* Numeric Keypad */}
        <div className="grid grid-cols-3 gap-1 mb-1">
          {/* Row 1 */}
          <button
            type="button"
            onClick={() => handleButtonClick('7')}
            disabled={disabled}
            className="px-2 py-2 bg-gray-300 hover:bg-gray-400 rounded text-sm font-semibold text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            7
          </button>
          <button
            type="button"
            onClick={() => handleButtonClick('8')}
            disabled={disabled}
            className="px-2 py-2 bg-gray-300 hover:bg-gray-400 rounded text-sm font-semibold text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            8
          </button>
          <button
            type="button"
            onClick={() => handleButtonClick('9')}
            disabled={disabled}
            className="px-2 py-2 bg-gray-300 hover:bg-gray-400 rounded text-sm font-semibold text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            9
          </button>

          {/* Row 2 */}
          <button
            type="button"
            onClick={() => handleButtonClick('4')}
            disabled={disabled}
            className="px-2 py-2 bg-gray-300 hover:bg-gray-400 rounded text-sm font-semibold text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            4
          </button>
          <button
            type="button"
            onClick={() => handleButtonClick('5')}
            disabled={disabled}
            className="px-2 py-2 bg-gray-300 hover:bg-gray-400 rounded text-sm font-semibold text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            5
          </button>
          <button
            type="button"
            onClick={() => handleButtonClick('6')}
            disabled={disabled}
            className="px-2 py-2 bg-gray-300 hover:bg-gray-400 rounded text-sm font-semibold text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            6
          </button>

          {/* Row 3 */}
          <button
            type="button"
            onClick={() => handleButtonClick('1')}
            disabled={disabled}
            className="px-2 py-2 bg-gray-300 hover:bg-gray-400 rounded text-sm font-semibold text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            1
          </button>
          <button
            type="button"
            onClick={() => handleButtonClick('2')}
            disabled={disabled}
            className="px-2 py-2 bg-gray-300 hover:bg-gray-400 rounded text-sm font-semibold text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            2
          </button>
          <button
            type="button"
            onClick={() => handleButtonClick('3')}
            disabled={disabled}
            className="px-2 py-2 bg-gray-300 hover:bg-gray-400 rounded text-sm font-semibold text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            3
          </button>
        </div>

        {/* Bottom Row: 0, ., - (Always show all three) */}
        <div className="grid grid-cols-3 gap-1 mb-1">
          <button
            type="button"
            onClick={() => handleButtonClick('0')}
            disabled={disabled}
            className="px-2 py-2 bg-gray-300 hover:bg-gray-400 rounded text-sm font-semibold text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            0
          </button>
          <button
            type="button"
            onClick={() => handleButtonClick('.')}
            disabled={disabled || !allowDecimal}
            className="px-2 py-2 bg-gray-300 hover:bg-gray-400 rounded text-sm font-semibold text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            .
          </button>
          <button
            type="button"
            onClick={() => handleButtonClick('-')}
            disabled={disabled || !allowNegative}
            className="px-2 py-2 bg-gray-300 hover:bg-gray-400 rounded text-sm font-semibold text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            -
          </button>
        </div>

        {/* Navigation Arrows */}
        <div className="grid grid-cols-2 gap-1 mb-1">
          <button
            type="button"
            onClick={handleCursorLeft}
            disabled={disabled}
            className="px-2 py-1.5 bg-gray-300 hover:bg-gray-400 rounded flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4 text-black" />
          </button>
          <button
            type="button"
            onClick={handleCursorRight}
            disabled={disabled}
            className="px-2 py-1.5 bg-gray-300 hover:bg-gray-400 rounded flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-4 h-4 text-black" />
          </button>
        </div>

        {/* Clear All Button */}
        <button
          type="button"
          onClick={handleClearAll}
          disabled={disabled}
          className="w-full px-2 py-1.5 bg-gray-300 hover:bg-gray-400 rounded text-sm font-medium text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Clear All
        </button>
      </div>
    </div>
  );
});

// Enhanced Multiple Choice Question Component
export const MultipleChoiceQuestion = ({ 
  question, 
  selectedAnswer, 
  onAnswerSelect, 
  isReview = false,
  correctAnswer = null 
}) => {
  const [selectedOptions, setSelectedOptions] = useState(
    selectedAnswer !== null && selectedAnswer !== undefined 
      ? (Array.isArray(selectedAnswer) ? selectedAnswer : [selectedAnswer]) 
      : []
  );

  useEffect(() => {
    const newSelection = selectedAnswer !== null && selectedAnswer !== undefined 
      ? (Array.isArray(selectedAnswer) ? selectedAnswer : [selectedAnswer]) 
      : [];
    setSelectedOptions(newSelection);
  }, [selectedAnswer, question.id]);

  const handleOptionSelect = (optionIndex) => {
    if (isReview) return;

    let newSelection;
    if (question.allowMultiple) {
      // Multiple selection
      newSelection = selectedOptions.includes(optionIndex)
        ? selectedOptions.filter(i => i !== optionIndex)
        : [...selectedOptions, optionIndex];
    } else {
      // Single selection - allow deselection
      newSelection = selectedOptions.includes(optionIndex)
        ? [] // Deselect if already selected
        : [optionIndex]; // Select if not selected
    }

    setSelectedOptions(newSelection);
    onAnswerSelect(question.id, question.allowMultiple ? newSelection : (newSelection.length > 0 ? newSelection[0] : null));
  };

  const isOptionSelected = (optionIndex) => {
    // Use local state as the primary source since it's updated immediately
    const isSelected = selectedOptions.includes(optionIndex);
    return isSelected;
  };

  const getOptionStyle = (optionIndex) => {
    // No borders, just clean white background
    return 'bg-white text-gray-900';
  };

  return (
    <div className="space-y-3">
      {/* Question Image */}
      {question.questionImageUrl && (
        <div className="mb-4">
          <OptimizedImage
            src={question.questionImageUrl}
            alt="Question"
            className="max-w-full h-auto max-h-96 rounded-lg border shadow-sm"
            fallback="/images/placeholder.svg"
          />
        </div>
      )}
      
      {question.options && question.options.length > 0 ? (
        question.options.map((option, index) => (
          <label
            key={index}
            className={`flex items-start py-2 cursor-pointer transition-colors ${
              isReview ? 'cursor-default' : 'cursor-pointer'
            } ${getOptionStyle(index)}`}
          >
            <input
              type={question.allowMultiple ? "checkbox" : "radio"}
              name={`question-${question.id}`}
              value={index}
              checked={isOptionSelected(index)}
              onChange={() => handleOptionSelect(index)}
              className="mt-1 mr-3"
              style={{
                width: '16px',
                height: '16px',
                minWidth: '16px',
                minHeight: '16px',
                transform: 'scale(1.0)',
                transformOrigin: 'left center',
                marginRight: '4px'
              }}
              disabled={isReview}
            />
            <div className="flex-1">
              <div>
                <KatexRenderer content={option} />
              </div>
              {/* Option Image */}
              {question.optionImageUrls && question.optionImageUrls[index] && (
                <div className="mt-2">
                  <OptimizedImage
                    src={question.optionImageUrls[index]}
                    alt={`Option ${index + 1}`}
                    className="max-w-full h-auto max-h-32 rounded border"
                    fallback="/images/placeholder.svg"
                  />
                </div>
              )}
            </div>
          </label>
        ))
      ) : (
        <div className="p-4 border border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
            <span className="text-yellow-800 dark:text-yellow-200">
              No answer options available for this question. This may be due to incomplete data.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// Integer Type Question Component
export const IntegerQuestion = ({ 
  question, 
  selectedAnswer, 
  onAnswerSelect, 
  isReview = false 
}) => {
  const [value, setValue] = useState(selectedAnswer !== null && selectedAnswer !== undefined ? String(selectedAnswer) : '');
  const [error, setError] = useState('');

  useEffect(() => {
    setValue(selectedAnswer !== null && selectedAnswer !== undefined ? String(selectedAnswer) : '');
  }, [selectedAnswer]);

  const validateInput = (inputValue) => {
    // Allow only integers
    if (inputValue === '' || /^-?\d+$/.test(inputValue)) {
      setError('');
      
      if (inputValue !== '') {
        const numValue = parseInt(inputValue);
        
        // Check range validation if specified
        if (question.minValue !== undefined && numValue < question.minValue) {
          setError(`Value must be at least ${question.minValue}`);
          return { isValid: false };
        } else if (question.maxValue !== undefined && numValue > question.maxValue) {
          setError(`Value must be at most ${question.maxValue}`);
          return { isValid: false };
        } else {
          onAnswerSelect(question.id, numValue);
          return { isValid: true };
        }
      } else {
        onAnswerSelect(question.id, null);
        return { isValid: true };
      }
    }
    return { isValid: false };
  };

  const handleValueChange = (newValue) => {
    setValue(newValue);
    validateInput(newValue);
  };

  return (
    <div className="space-y-3">
      {/* Question Image */}
      {question.questionImageUrl && (
        <div className="mb-4">
          <OptimizedImage
            src={question.questionImageUrl}
            alt="Question"
            className="max-w-full h-auto max-h-96 rounded-lg border shadow-sm"
            fallback="/images/placeholder.svg"
          />
        </div>
      )}
      
      <div>
        <div className="relative">
          <CalculatorInput
            value={value}
            onChange={handleValueChange}
            onValidation={validateInput}
            disabled={isReview}
            allowDecimal={false}
            allowNegative={true}
            placeholder="Enter your answer"
            unit={question.unit}
          />
        </div>
      </div>
      {error && <p className="text-red-600 text-sm">{error}</p>}
      {question.hint && (
        <p className="text-gray-700 dark:text-gray-300 text-sm italic">{question.hint}</p>
      )}
    </div>
  );
};

// Floating Point Question Component
export const FloatingPointQuestion = ({ 
  question, 
  selectedAnswer, 
  onAnswerSelect, 
  isReview = false 
}) => {
  const [value, setValue] = useState(selectedAnswer !== null && selectedAnswer !== undefined ? String(selectedAnswer) : '');
  const [error, setError] = useState('');

  useEffect(() => {
    setValue(selectedAnswer !== null && selectedAnswer !== undefined ? String(selectedAnswer) : '');
  }, [selectedAnswer]);

  const validateInput = (inputValue) => {
    // Allow decimals (simplified - no scientific notation in calculator input)
    if (inputValue === '' || /^-?\d*\.?\d*$/.test(inputValue)) {
      setError('');
      
      if (inputValue !== '' && inputValue !== '-' && inputValue !== '.') {
        const numValue = parseFloat(inputValue);
        
        if (!isNaN(numValue)) {
          // Check precision
          const decimalPlaces = (inputValue.split('.')[1] || '').length;
          if (question.maxPrecision && decimalPlaces > question.maxPrecision) {
            setError(`Maximum ${question.maxPrecision} decimal places allowed`);
            return { isValid: false };
          }
          
          // Check range validation
          if (question.minValue !== undefined && numValue < question.minValue) {
            setError(`Value must be at least ${question.minValue}`);
            return { isValid: false };
          } else if (question.maxValue !== undefined && numValue > question.maxValue) {
            setError(`Value must be at most ${question.maxValue}`);
            return { isValid: false };
          } else {
            onAnswerSelect(question.id, numValue);
            return { isValid: true };
          }
        }
      } else if (inputValue === '') {
        onAnswerSelect(question.id, null);
        return { isValid: true };
      } else {
        // Allow partial input like "-" or "."
        return { isValid: true };
      }
    }
    return { isValid: false };
  };

  const handleValueChange = (newValue) => {
    setValue(newValue);
    validateInput(newValue);
  };

  return (
    <div className="space-y-3">
      {/* Question Image */}
      {question.questionImageUrl && (
        <div className="mb-4">
          <OptimizedImage
            src={question.questionImageUrl}
            alt="Question"
            className="max-w-full h-auto max-h-96 rounded-lg border shadow-sm"
            fallback="/images/placeholder.svg"
          />
        </div>
      )}
      
      <div>
        <CalculatorInput
          value={value}
          onChange={handleValueChange}
          onValidation={validateInput}
          disabled={isReview}
          allowDecimal={true}
          allowNegative={true}
          placeholder="Enter your answer"
          unit={question.unit}
        />
      </div>
      {error && <p className="text-red-600 text-sm">{error}</p>}
      {question.hint && (
        <p className="text-gray-700 dark:text-gray-300 text-sm italic">{question.hint}</p>
      )}
      {question.maxPrecision && (
        <p className="text-gray-600 dark:text-gray-400 text-sm">
          Maximum {question.maxPrecision} decimal places
        </p>
      )}
    </div>
  );
};

// Subjective Question Component
export const SubjectiveQuestion = ({ 
  question, 
  selectedAnswer, 
  onAnswerSelect, 
  isReview = false 
}) => {
  const MAX_WORDS = 1000; // Default 1000 word limit for subjective tests
  const textareaRef = useRef(null);
  
  // Use question.maxWords if provided, otherwise default to 1000
  const maxWords = question.maxWords || MAX_WORDS;
  
  // Calculate word count from plain text
  const getWordCount = (text) => {
    if (!text || text.trim() === '') return 0;
    const words = text.trim().split(/\s+/).filter(word => word.length > 0);
    return words.length;
  };

  const wordCount = getWordCount(selectedAnswer || '');
  const charCount = (selectedAnswer || '').length;
  const isWordLimitExceeded = wordCount > maxWords;

  const handleTextChange = (e) => {
    const newText = e.target.value;
    const newWordCount = getWordCount(newText);
    
    // Prevent typing if word limit is exceeded
    // Allow deletion even if over limit
    if (newWordCount > maxWords && newText.length > (selectedAnswer || '').length) {
      // User is trying to add more text, prevent it
      return;
    }
    
    if (!isReview) {
      onAnswerSelect(question.id, newText);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = (e.clipboardData || window.clipboardData).getData('text');
    const currentText = selectedAnswer || '';
    const newText = currentText + pastedText;
    const newWordCount = getWordCount(newText);
    
    // Only allow paste if it doesn't exceed word limit
    if (newWordCount <= maxWords) {
      if (!isReview) {
        onAnswerSelect(question.id, newText);
      }
    } else {
      // Truncate pasted text to fit within word limit
      const words = newText.trim().split(/\s+/).filter(word => word.length > 0);
      const truncatedWords = words.slice(0, maxWords);
      const truncatedText = truncatedWords.join(' ');
      if (!isReview) {
        onAnswerSelect(question.id, truncatedText);
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Question Image */}
      {question.questionImageUrl && (
        <div className="mb-4">
          <OptimizedImage
            src={question.questionImageUrl}
            alt="Question"
            className="max-w-full h-auto max-h-96 rounded-lg border shadow-sm"
            fallback="/images/placeholder.svg"
          />
        </div>
      )}
      
      <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
        <textarea
          ref={textareaRef}
          value={selectedAnswer || ''}
          onChange={handleTextChange}
          onPaste={handlePaste}
          disabled={isReview}
          placeholder="Type your answer here..."
          className="w-full p-4 min-h-[300px] resize-y border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400 font-sans text-sm leading-relaxed"
          style={{
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontSize: '14px',
            lineHeight: '1.6'
          }}
        />
      </div>
      
      <div className="flex justify-between items-center text-sm">
        <div className="flex items-center gap-4">
          <div className={`${isWordLimitExceeded ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
            <span className="font-medium">Words: {wordCount}</span>
            <span className={`ml-1 ${isWordLimitExceeded ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
              / {maxWords}
            </span>
          </div>
          <div className="text-gray-500 dark:text-gray-400">
            Characters: {charCount}
          </div>
        </div>
        {isWordLimitExceeded && (
          <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
            <AlertCircle className="w-4 h-4" />
            <span className="text-xs font-medium">Word limit exceeded</span>
          </div>
        )}
      </div>
      
      {question.hint && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <p className="text-blue-800 dark:text-blue-200 text-sm italic">
            <strong>Hint:</strong> {question.hint}
          </p>
        </div>
      )}
    </div>
  );
};

// Matrix Match Question Component
export const MatrixMatchQuestion = ({ 
  question, 
  selectedAnswer, 
  onAnswerSelect, 
  isReview = false 
}) => {
  const [matches, setMatches] = useState(selectedAnswer || {});

  useEffect(() => {
    setMatches(selectedAnswer || {});
  }, [selectedAnswer]);

  const handleMatch = (columnAIndex, columnBIndex) => {
    if (isReview) return;

    const newMatches = { ...matches };
    
    // Remove existing match for this column A item
    Object.keys(newMatches).forEach(key => {
      if (newMatches[key] === columnBIndex) {
        delete newMatches[key];
      }
    });
    
    // Add new match
    if (matches[columnAIndex] === columnBIndex) {
      delete newMatches[columnAIndex];
    } else {
      newMatches[columnAIndex] = columnBIndex;
    }
    
    setMatches(newMatches);
    onAnswerSelect(question.id, newMatches);
  };

  const isMatched = (columnAIndex, columnBIndex) => {
    return matches[columnAIndex] === columnBIndex;
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-8">
        {/* Column A */}
        <div>
          <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Column A</h4>
          <div className="space-y-2">
            {question.columnA.map((item, index) => (
              <div
                key={index}
                className="p-3 border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600"
              >
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {index + 1}.
                </span>
                <span className="ml-2 text-gray-900 dark:text-gray-100">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Column B */}
        <div>
          <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Column B</h4>
          <div className="space-y-2">
            {question.columnB.map((item, index) => (
              <div
                key={index}
                className="p-3 border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600"
              >
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {index + 1}.
                </span>
                <span className="ml-2 text-gray-900 dark:text-gray-100">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Matching Interface */}
      <div className="border rounded-lg p-4 bg-white dark:bg-gray-900">
        <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Match Column A with Column B</h4>
        <div className="grid grid-cols-2 gap-4">
          {question.columnA.map((_, columnAIndex) => (
            <div key={columnAIndex} className="space-y-2">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {columnAIndex + 1} →
              </div>
              <div className="grid grid-cols-5 gap-1">
                {question.columnB.map((_, columnBIndex) => (
                  <button
                    key={columnBIndex}
                    onClick={() => handleMatch(columnAIndex, columnBIndex)}
                    disabled={isReview}
                    className={`w-8 h-8 rounded border text-sm font-medium transition-colors ${
                      isMatched(columnAIndex, columnBIndex)
                        ? 'bg-blue-500 border-blue-500 text-white'
                        : 'border-gray-400 hover:border-blue-400 dark:border-gray-500 dark:hover:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-blue-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    {columnBIndex + 1}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Assertion-Reason Question Component
export const AssertionReasonQuestion = ({ 
  question, 
  selectedAnswer, 
  onAnswerSelect, 
  isReview = false 
}) => {
  const handleOptionSelect = (optionIndex) => {
    if (isReview) return;
    onAnswerSelect(question.id, optionIndex);
  };

  const getOptionStyle = (optionIndex) => {
    if (isReview) {
      const isCorrect = question.correctAnswer === optionIndex;
      const isSelected = selectedAnswer === optionIndex;
      
      if (isCorrect) {
        return 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-900 dark:text-green-100';
      } else if (isSelected && !isCorrect) {
        return 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-100';
      }
    }

    return selectedAnswer === optionIndex
      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-900 dark:text-blue-100'
      : 'border-gray-300 hover:border-blue-400 dark:border-gray-600 dark:hover:border-blue-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-blue-50 dark:hover:bg-gray-700';
  };

  const options = [
    'Both Assertion and Reason are true and Reason is the correct explanation of Assertion',
    'Both Assertion and Reason are true but Reason is not the correct explanation of Assertion',
    'Assertion is true but Reason is false',
    'Assertion is false but Reason is true'
  ];

  return (
    <div className="space-y-4">
      {/* Assertion */}
      <div className="p-4 border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/20">
        <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Assertion:</h4>
        <div className="text-gray-700 dark:text-gray-300">
          <KatexRenderer content={question.assertion} />
        </div>
      </div>

      {/* Reason */}
      <div className="p-4 border-l-4 border-green-500 bg-green-50 dark:bg-green-900/20">
        <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Reason:</h4>
        <div className="text-gray-700 dark:text-gray-300">
          <KatexRenderer content={question.reason} />
        </div>
      </div>

      {/* Options */}
      <div className="space-y-3">
        {options.map((option, index) => (
          <label
            key={index}
            className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${
              isReview ? 'cursor-default' : 'cursor-pointer'
            } ${getOptionStyle(index)}`}
          >
            <input
              type="radio"
              name={`question-${question.id}`}
              value={index}
              checked={selectedAnswer === index}
              onChange={() => handleOptionSelect(index)}
              className="sr-only"
              disabled={isReview}
            />
            <div className={`border rounded-full mr-3 mt-0.5 flex items-center justify-center ${
              selectedAnswer === index
                ? 'border-blue-500 bg-blue-500'
                : 'border-gray-400 dark:border-gray-500 bg-white dark:bg-gray-700'
            }`}
            style={{
              width: '24px',
              height: '24px',
              minWidth: '24px',
              minHeight: '24px',
              borderWidth: '2px'
            }}>
              {selectedAnswer === index && (
                <div className="bg-white rounded-full" style={{
                  width: '12px',
                  height: '12px',
                  minWidth: '12px',
                  minHeight: '12px'
                }}></div>
              )}
            </div>
            <span className="text-gray-900 dark:text-gray-100">{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

// Main Question Component that renders based on type
export const QuestionComponent = ({ 
  question, 
  selectedAnswer, 
  onAnswerSelect, 
  isReview = false,
  correctAnswer = null 
}) => {
  switch (question.type) {
    case 'multiple-choice':
    case 'rc': // RC questions use multiple choice format
    case 'lrdi-set': // LRDI Set questions use multiple choice format
      return (
        <MultipleChoiceQuestion
          question={question}
          selectedAnswer={selectedAnswer}
          onAnswerSelect={onAnswerSelect}
          isReview={isReview}
          correctAnswer={correctAnswer}
        />
      );
    
    case 'integer':
      return (
        <IntegerQuestion
          question={question}
          selectedAnswer={selectedAnswer}
          onAnswerSelect={onAnswerSelect}
          isReview={isReview}
        />
      );
    
    case 'floating-point':
      return (
        <FloatingPointQuestion
          question={question}
          selectedAnswer={selectedAnswer}
          onAnswerSelect={onAnswerSelect}
          isReview={isReview}
        />
      );
    
    case 'subjective':
      return (
        <SubjectiveQuestion
          question={question}
          selectedAnswer={selectedAnswer}
          onAnswerSelect={onAnswerSelect}
          isReview={isReview}
        />
      );
    
    case 'matrix-match':
      return (
        <MatrixMatchQuestion
          question={question}
          selectedAnswer={selectedAnswer}
          onAnswerSelect={onAnswerSelect}
          isReview={isReview}
        />
      );
    
    case 'assertion-reason':
      return (
        <AssertionReasonQuestion
          question={question}
          selectedAnswer={selectedAnswer}
          onAnswerSelect={onAnswerSelect}
          isReview={isReview}
        />
      );
    
    default:
      // Fallback to multiple choice for backward compatibility
      return (
        <MultipleChoiceQuestion
          question={question}
          selectedAnswer={selectedAnswer}
          onAnswerSelect={onAnswerSelect}
          isReview={isReview}
          correctAnswer={correctAnswer}
        />
      );
  }
};
